import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { getThemePreference, saveThemePreference } from "../services/userPreferences";

export type ThemeName = "green" | "blue" | "purple" | "orange";

interface ThemeContextType {
  currentTheme: ThemeName;
  changeTheme: (theme: ThemeName) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const themes: Record<ThemeName, { name: string; color: string }> = {
  green: { name: "Green", color: "#35ba5b" },
  blue: { name: "Blue", color: "#3b82f6" },
  purple: { name: "Purple", color: "#a855f7" },
  orange: { name: "Orange", color: "#f97316" },
};

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [currentTheme, setCurrentTheme] = useState<ThemeName>(() => {
    return getThemePreference();
  });

  useEffect(() => {
    // Apply theme to document root immediately on mount
    const theme = getThemePreference();
    document.documentElement.setAttribute("data-theme", theme);
  }, []); // Run only on mount

  useEffect(() => {
    // Apply theme to document root when theme changes
    document.documentElement.setAttribute("data-theme", currentTheme);
    // Save to unified preferences service
    saveThemePreference(currentTheme);
  }, [currentTheme]);

  const changeTheme = (theme: ThemeName) => {
    setCurrentTheme(theme);
  };

  return (
    <ThemeContext.Provider value={{ currentTheme, changeTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};

export { themes };
